// CMS9 added the LabeledCheckboxView, patterned off of the LabeledFieldView.

import LabelView from '@ckeditor/ckeditor5-ui/src/label/labelview';
import View from '@ckeditor/ckeditor5-ui/src/view';
import uid from '@ckeditor/ckeditor5-utils/src/uid';

// CMS9 added these references.
import TooltipView from '@ckeditor/ckeditor5-ui/src/tooltip/tooltipview';
import { getEnvKeystrokeText } from '@ckeditor/ckeditor5-utils/src/keyboard';
import CheckboxView from './checkboxview';

/**
 * The labeled input view class. It can be used to enhance any view with the following features:
 *
 * * a label,
 * * (optional) an error message,
 * * (optional) a collection of choosers for the input.
 * * (optional) an info (status) text,
 *
 * all bound logically by proper DOM attributes for UX and accessibility.  It also provides an interface
 * (e.g. observable properties) that allows controlling those additional features.
 *
 * The constructor of this class requires a callback that returns a view to be labeled. The callback
 * is called with unique ids that allow binding of DOM properties:
 *
 *		const labeledInputView = new LabeledFieldView( locale, ( labeledFieldView, viewUid, statusUid ) => {
 *			const inputView = new InputTextView( labeledFieldView.locale );
 *
 *			inputView.set( {
 *				id: viewUid,
 *				ariaDescribedById: statusUid
 *			} );
 *
 *			inputView.bind( 'isReadOnly' ).to( labeledFieldView, 'isEnabled', value => !value );
 *			inputView.bind( 'hasError' ).to( labeledFieldView, 'errorText', value => !!value );
 *
 *			return inputView;
 *		} );
 *
 *		labeledInputView.label = 'User name';
 *		labeledInputView.infoText = 'Full name like for instance, John Doe.';
 *		labeledInputView.render();
 *
 *		document.body.append( labeledInputView.element );
 *
 * See {@link module:ui/labeledfield/utils} to discover ready–to–use labeled input helpers for common
 * UI components.
 *
 * @extends module:ui/view~View
 */

export default class LabeledCheckboxView extends View {
	/**
	 * Creates an instance of the labeled checkbox view class using a provided creator function
	 * that provides the view to be labeled.
	 *
	 * @param {module:utils/locale~Locale} locale The locale instance.
	 */
	constructor( locale ) {
		super( locale );

		// CMS9 changed the class name.
		const viewUid = `ck-labeled-checkbox-view-${ uid() }`;
		const statusUid = `ck-labeled-checkbox-view-status-${ uid() }`;

		/**
		 * Is the view in a 'on' state.
		 *
		 * @observable
		 * @member {boolean} #label
		 */
		this.set( 'isOn' );

		/**
		 * The field view that gets labeled.
		 *
		 * @member {module:ui/view~View} #fieldView
		 */
		this.fieldView = this._createCheckbox( viewUid, statusUid );

		/**
		 * The text of the label.
		 *
		 * @observable
		 * @member {String} #label
		 */
		this.set( 'label' );

		/**
		 * Controls whether the component is in read-only mode.
		 *
		 * @observable
		 * @member {Boolean} #isEnabled
		 */
		this.set( 'isEnabled', true );

		// CMS9 removed isEmpty.

		/**
		 * An observable flag set to `true` when {@link #fieldView} is currently focused by
		 * the user (`false` otherwise).
		 *
		 * @readonly
		 * @observable
		 * @member {Boolean} #isFocused
		 * @default false
		 */
		this.set( 'isFocused', false );

		/**
		 * The validation error text. When set, it will be displayed
		 * next to the {@link #fieldView} as a typical validation error message.
		 * Set it to `null` to hide the message.
		 *
		 * **Note:** Setting this property to anything but `null` will automatically
		 * make the `hasError` of the {@link #fieldView} `true`.
		 *
		 * @observable
		 * @member {String|null} #errorText
		 */
		this.set( 'errorText', null );

		/**
		 * The additional information text displayed next to the {@link #fieldView} which can
		 * be used to inform the user about its purpose, provide help or hints.
		 *
		 * Set it to `null` to hide the message.
		 *
		 * **Note:** This text will be displayed in the same place as {@link #errorText} but the
		 * latter always takes precedence: if the {@link #errorText} is set, it replaces
		 * {@link #infoText}.
		 *
		 * @observable
		 * @member {String|null} #infoText
		 * @default null
		 */
		this.set( 'infoText', null );

		/**
		 * (Optional) The additional CSS class set on the dropdown {@link #element}.
		 *
		 * @observable
		 * @member {String} #class
		 */
		this.set( 'class' );

		// CMS9 set the tooltip.
		this.set( 'tooltip' );
		this.set( 'tooltipPosition', 's' );

		/**
		 * The label view instance that describes the entire view.
		 *
		 * @member {module:ui/label/labelview~LabelView} #labelView
		 */
		this.labelView1 = this._createLabelView( viewUid, true );
		this.labelView2 = this._createLabelView( viewUid, false );
		// CMS9 create two labelviews.

		// CMS9 added the tooltip view.
		this.tooltipView = this._createTooltipView();

		/**
		 * The status view for the {@link #fieldView}. It displays {@link #errorText} and
		 * {@link #infoText}.
		 *
		 * @member {module:ui/view~View} #statusView
		 */
		this.statusView = this._createStatusView( statusUid );

		/**
		 * The combined status text made of {@link #errorText} and {@link #infoText}.
		 * Note that when present, {@link #errorText} always takes precedence in the
		 * status.
		 *
		 * @see #errorText
		 * @see #infoText
		 * @see #statusView
		 * @private
		 * @observable
		 * @member {String|null} #_statusText
		 */
		this.bind( '_statusText' ).to(
			this, 'errorText',
			this, 'infoText',
			( errorText, infoText ) => errorText || infoText
		);

		// CMS9 added the tooltip binding.
		this.bind( '_tooltipString' ).to(
			this, 'tooltip',
			this, 'label',
			this, 'keystroke', this._getTooltipString.bind( this )
		);

		const bind = this.bindTemplate;

		// CMS9 changed the class names.
		this.setTemplate( {
			tag: 'div',
			attributes: {
				class: [
					// CMS9 changed the class name.
					'ck',
					'ck-labeled-checkbox-view',
					bind.to( 'class' ),
					bind.if( 'isEnabled', 'ck-disabled', value => !value ),
					bind.if( 'isFocused', 'ck-labeled-checkbox-view_focused' ),
					bind.if( 'errorText', 'ck-error' )
				]
			},
			children: [
				// CMS9 changed the children.
				this.tooltipView,
				this.fieldView,
				this.labelView1,
				this.labelView2,
				this.statusView
			]
		} );

		// CMS9 wire on two-way binding (and prevent recursion).
		this.on( 'change:isOn', _ => {
			const isOn = !!this.isOn;
			const checked = !!this.fieldView.checked;
			if ( isOn !== checked ) {
				this.fieldView.checked = isOn;
			}
		} );
		this.fieldView.on( 'change:checked', _ => {
			const checked = !!this.fieldView.checked;
			const isOn = !!this.isOn;
			if ( checked !== isOn ) {
				this.isOn = checked;
			}
		} );
	}

	// CMS9 added.
	/**
	 * Create the checkbox.
	 *
	 * @param {string} viewId
	 * @param {string} statusUid
	 * @returns
	 */
	_createCheckbox( viewId, statusUid  ){
		const checkboxView = new CheckboxView( this.locale );
		checkboxView.set( {
			id: viewId,
			ariaDescribedById: statusUid
		} );

		checkboxView.bind( 'isReadOnly' ).to( this, 'isEnabled', value => !value );
		checkboxView.bind( 'hasError' ).to( this, 'errorText', value => !!value );

		checkboxView.on( 'input', () => {
			// UX: Make the error text disappear and disable the error indicator as the user
			// starts fixing the errors.
			this.errorText = null;
		} );

		return checkboxView;
	}

	// CMS9 modified the label view with an option to create a cosmetic "replace" label.
	/**
	 * Creates label view class instance and bind with view.
	 *
	 * @private
	 * @param {String} id Unique id to set as labelView#for attribute.
	 * @param {boolean} replace Render the aesthetic checkbox replacement label.
	 * @returns {module:ui/label/labelview~LabelView}
	 */
	_createLabelView( id, replace ) {
		const labelView = new LabelView( this.locale );

		labelView.for = id;
		// CMS9 added the replace option.
		if ( replace ) {
			labelView.extendTemplate( {
				attributes: {
					class: 'ck-checkbox-replace'
				}
			} );
		} else {
			labelView.bind( 'text' ).to( this, 'label' );
		}

		return labelView;
	}

	/**
	 * Creates the status view instance. It displays {@link #errorText} and {@link #infoText}
	 * next to the {@link #fieldView}. See {@link #_statusText}.
	 *
	 * @private
	 * @param {String} statusUid Unique id of the status, shared with the {@link #fieldView view's}
	 * `aria-describedby` attribute.
	 * @returns {module:ui/view~View}
	 */
	_createStatusView( statusUid ) {
		const statusView = new View( this.locale );
		const bind = this.bindTemplate;

		statusView.setTemplate( {
			tag: 'div',
			attributes: {
				class: [
					// CMS9 changed the class name.
					'ck',
					'ck-labeled-checkbox-view__status',
					bind.if( 'errorText', 'ck-labeled-checkbox-view__status_error' ),
					bind.if( '_statusText', 'ck-hidden', value => !value )
				],
				id: statusUid,
				role: bind.if( 'errorText', 'alert' )
			},
			children: [
				{
					text: bind.to( '_statusText' )
				}
			]
		} );

		return statusView;
	}

	// CMS9 added method.
	/**
	 * Create a tooltip view for the checkbox.
	 *
	 * @returns TooltipView
	 */
	_createTooltipView() {
		const tooltipView = new TooltipView( this.locale );
		tooltipView.bind( 'text' ).to( this, '_tooltipString' );
		tooltipView.bind( 'position' ).to( this, 'tooltipPosition' );
		return tooltipView;
	}

	// CMS9 added method.
	/**
	 * Gets the text for the {@link #tooltipView} from the combination of
	 * {@link #tooltip}, {@link #label} and {@link #keystroke} attributes.
	 *
	 * @private
	 * @see #tooltip
	 * @see #_tooltipString
	 * @param {Boolean|String|Function} tooltip Button tooltip.
	 * @param {String} label Button label.
	 * @param {String} keystroke Button keystroke.
	 * @returns {String}
	 */
	_getTooltipString( tooltip, label, keystroke ) {
		if ( tooltip ) {
			if ( typeof tooltip == 'string' ) {
				return tooltip;
			} else {
				if ( keystroke ) {
					keystroke = getEnvKeystrokeText( keystroke );
				}

				if ( tooltip instanceof Function ) {
					return tooltip( label, keystroke );
				} else {
					return `${ label }${ keystroke ? ` (${ keystroke })` : '' }`;
				}
			}
		}

		return '';
	}

	/**
	 * Focuses the {@link #fieldView}.
	 */
	focus() {
		this.fieldView.focus();
	}
}
